import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { twinSetupPath } from '@repo/ui';
import { useSpotlightStore, useTwinContext } from '@repo/front-end-state';

/** The parts of a twin record onboarding's address needs. Either can be missing on a bad load. */
type TwinRecord = { id?: number | null; bid?: string | null } | null | undefined;

const addressable = (twin: TwinRecord): twin is { id: number; bid: string } =>
  typeof twin?.id === 'number' && Number.isFinite(twin.id) && !!twin.bid?.trim();

/**
 * Where the mode switcher's *Edit* goes (`retire-edit-mode-entry`, R1): onboarding for the loaded
 * twin, through the wrench's own address. Only a TwinLabs admin goes anywhere, as with the wrench;
 * `/onboarding`'s route guard is still the enforcement. `null` means stay put: not an admin, no
 * twin record with an id and a BID to name, or a record that is not the selected twin's (an earlier
 * load that finished after a twin switch can leave one in context).
 */
export const editTwinTarget = ({
  isTlAdmin,
  adminTwin,
  selectedBid,
}: {
  isTlAdmin: boolean;
  adminTwin: TwinRecord;
  selectedBid: string | null;
}): string | null =>
  isTlAdmin && addressable(adminTwin) && adminTwin.bid === selectedBid
    ? twinSetupPath(adminTwin)
    : null;

/** Why a load sent the twin to onboarding, as logged. */
export type OnboardingReason = 'no-model' | 'empty' | 'parse-failed';

/**
 * What a load that cannot parse the twin does (`retire-edit-mode-entry`, R4 and R7). It reaches
 * this when the twin record, the graph model or the organisation's name is missing:
 * - `no-model`: the record loaded and the model did not (none yet, or the model service down).
 *   Onboarding is where a twin gets one, so an admin is sent there.
 * - `record-missing`: no twin record, so no twin to name in onboarding's address. The page stays.
 * - `no-organisation`: record and model both loaded but the signed-in user's organisation name has
 *   not. The twin has a model, so it is not sent to onboarding. The page stays.
 * The last two are logged so a blank page is never silent.
 */
export const unparsedLoadOutcome = ({
  hasRecord,
  hasModel,
}: {
  hasRecord: boolean;
  hasModel: boolean;
}): 'no-model' | 'record-missing' | 'no-organisation' =>
  !hasRecord ? 'record-missing' : !hasModel ? 'no-model' : 'no-organisation';

/**
 * The twin page's loads that used to switch an admin into Edit mode now open onboarding for the
 * twin instead (`retire-edit-mode-entry`, R4–R7). The returned `sendToOnboarding` is called when a
 * load finds no graph model, a model with no children, or a parse that returns nothing.
 *
 * It does nothing, and returns false, unless:
 * - the signed-in user's roles have loaded and they are a TwinLabs admin;
 * - the twin record names an id and a BID (otherwise one log line);
 * - the record is the page's twin NOW — a load that finishes after the page moved to another twin
 *   says nothing about the new one;
 * - the visit did not arrive through the `?edit=<BID>` handshake for this twin. Admin's Open FML
 *   link sends people to the panel on purpose, empty twins included, and bouncing them back would
 *   make FML unreachable for a twin with no model yet. The exemption is remembered after
 *   `useEditModeEntry` removes `edit` from the address, because a save in the panel reloads the
 *   twin; it ends when the page's twin changes;
 * - no switch to another twin is pending: while the address carries Admin's Open FML link
 *   (`spotlight` and `edit` naming the same twin), a load of any OTHER twin — the one a new tab
 *   restored from an earlier visit — leaves the page alone, logged `[twin] onboarding held for the
 *   link`. Sending that twin to onboarding would clear the spotlight and lose the link's twin;
 * - the twin page is still mounted. A load can outlive it (nothing cancels the fetch), and the
 *   router's `navigate` still works after unmount, so without this a load finishing after the person
 *   pressed Back, or after an overlapping load already redirected, would replace whatever page they
 *   are on now.
 *
 * It navigates with the history entry replaced, and deselects the twin and drops its data (and any
 * spotlight that would re-select it), so onboarding's *Back to your twins* and the browser's Back land on the twin
 * list instead of reloading the broken twin and bouncing straight back.
 * Everything it reads is held in refs, so it is stable and reads current values when an async load
 * finishes — `loadTwinIntoContext` depends on it, and a changing callback would re-run the load.
 */
export const useSendToOnboarding = ({
  twinBId,
  rolesKnown,
  isTlAdmin,
}: {
  /** The page's selected twin. */
  twinBId: string | null;
  /** The signed-in user's roles have loaded — until then "not an admin" means "not known yet". */
  rolesKnown: boolean;
  isTlAdmin: boolean;
}): ((adminTwin: TwinRecord, reason: OnboardingReason) => boolean) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(search);
  const handshakeBid = params.get('edit')?.trim();
  // Admin's Open FML link: the spotlight is switching the page to the twin `edit` names.
  const switchingTo =
    handshakeBid && params.get('spotlight')?.trim() === handshakeBid ? handshakeBid : null;

  const latest = useRef({ twinBId, rolesKnown, isTlAdmin, navigate, switchingTo });
  latest.current = { twinBId, rolesKnown, isTlAdmin, navigate, switchingTo };

  // Set in the effect, not only initially, so StrictMode's mount, unmount, mount leaves it true.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The handshake's exemption: which twin it is for, cleared when the page's twin changes.
  const exemptBid = useRef<string | null>(null);
  const exemptionTwin = useRef(twinBId);
  if (exemptionTwin.current !== twinBId) {
    exemptionTwin.current = twinBId;
    exemptBid.current = null;
  }
  if (handshakeBid && twinBId && handshakeBid === twinBId) {
    exemptBid.current = twinBId;
  }

  return useCallback((adminTwin: TwinRecord, reason: OnboardingReason) => {
    const {
      twinBId: currentBid,
      rolesKnown: known,
      isTlAdmin: admin,
      navigate: go,
      switchingTo: pending,
    } = latest.current;
    if (!mounted.current) return false;
    if (!known || !admin) return false;
    if (!addressable(adminTwin)) {
      // eslint-disable-next-line no-console
      console.info('[twin] onboarding address unavailable', {
        reason,
        twinBid: adminTwin?.bid ?? null,
      });
      return false;
    }
    if (adminTwin.bid !== currentBid) return false;
    if (pending && adminTwin.bid !== pending) {
      // eslint-disable-next-line no-console
      console.info('[twin] onboarding held for the link', {
        reason,
        twinBid: adminTwin.bid,
        requestedBid: pending,
      });
      return false;
    }
    if (exemptBid.current === adminTwin.bid) return false;
    // eslint-disable-next-line no-console
    console.info('[twin] sent to onboarding', { reason, twinBid: adminTwin.bid });
    go(twinSetupPath(adminTwin), { replace: true });
    useSpotlightStore.getState().clearSpotlight();
    useTwinContext.getState().setTwin(null);
    useTwinContext.getState().setTwinBId(null);
    return true;
  }, []);
};
